import { useEffect, useRef } from "react";
import { trackBlockView, trackBlockDwell } from "../lib/analytics";

/**
 * Fires `block_viewed` after the block is ≥50% visible for 1 s,
 * and `block_dwell` with total ms when it leaves the viewport.
 */
export function useBlockVisibility(name: string, index: number) {
  const ref = useRef<HTMLElement | null>(null);
  const enteredAt = useRef<number | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let timer: number | undefined;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            if (enteredAt.current == null) enteredAt.current = performance.now();
            if (!fired.current) {
              timer = window.setTimeout(() => {
                if (enteredAt.current != null) {
                  trackBlockView(name, index);
                  fired.current = true;
                }
              }, 1000);
            }
          } else {
            if (timer) { clearTimeout(timer); timer = undefined; }
            if (enteredAt.current != null) {
              const dwell = performance.now() - enteredAt.current;
              if (fired.current && dwell > 500) trackBlockDwell(name, Math.round(dwell));
              enteredAt.current = null;
            }
          }
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [name, index]);

  return ref;
}
